import React, {useEffect, useState} from "react";
import PropTypes from 'prop-types';
import {connect} from "react-redux";
import {bindActionCreators} from "redux";
import loadScript from 'load-script';
import {useParams, useHistory, useLocation} from "react-router-dom";
import {push} from "react-router-redux";
import SvgIcon from "@material-ui/core/SvgIcon";
import OrderForm from './OrderForm';
import PaymentForm from './PaymentForm';
import CardCheckoutSections from '../../constants/CardCheckoutSections';
import Errors from "../../utils/Errors";
import CardCheckoutErrorCodes from "../../constants/CardCheckoutErrorCodes";
import {FormattedMessage, IntlProvider} from "react-intl";
import buildPricingRequest from '../../utils/buildPricingRequest';
import StyleableSection from "../../components/StyleableSection";
import {CircularProgress, MuiThemeProvider, Typography, withStyles} from "@material-ui/core";
import CheckoutAPI from "../../api/CheckoutAPI";
import resolveCardDesignFromUrl from "./resolveCardDesignFromUrl";
import DeliverTimes from "../../constants/DeliverTimes";
import validateOrder from "./validateOrder";
import BusinessBanner from "./BusinessBanner";
import ConfirmationForm from "./ConfirmationForm";
import { uuid } from 'uuidv4';
import updateCheckoutSettings from "../../actions/updateCheckoutSettings";
import resolveLocaleFromProgram from "../../actions/resolveLocaleFromProgram";
import {yellow} from "@material-ui/core/colors";
import setShopifyMode from "../../actions/setShopifyMode";
import {GoogleReCaptchaProvider} from "react-google-recaptcha-v3";
import messages from "../../constants/Messages";
import CheckoutActions from "../../constants/CheckoutActions";
import validateReload from "./validateReload";

const styles = (theme) => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    height: '100%'
  },
  loading: {
    maxWidth: '50%',
  },
  svgIcon: {
    width: 128,
    height: 128,
    margin: '0 auto'
  },
  checkoutClosedHeader: {
    marginTop: 16,
    fontWeight: 300,
  },
  checkoutClosedMessage: {
    margin: 8,
    textAlign: 'center'
  },
  testModeBanner: {
    backgroundColor: yellow[700],
    width: '100%',
    fontSize: 12,
    paddingTop: 4,
    paddingBottom: 4,
    textAlign: 'center'
  },
});

const mapStateToProps = (state) => {
  const {locale, checkout, theme, shopifyMode,} = state;
  return {
    locale: locale.resolvedLocale,
    checkoutSettings: checkout.checkoutSettings,
    theme: theme.theme,
    shopifyMode: shopifyMode.shopifyMode,
    shopifySettings: shopifyMode.shopifySettings
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    actions: bindActionCreators({
      push,
      updateCheckoutSettings,
      resolveLocaleFromProgram,
      setShopifyMode
    }, dispatch)
  };
};

Checkout.propTypes = {
  actions: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  theme: PropTypes.object,
  checkoutSettings: PropTypes.object.isRequired,
  shopifyMode: PropTypes.bool.isRequired,
  shopifySettings: PropTypes.object,
  classes: PropTypes.object
};

function Checkout({locale = 'en-US', checkoutSettings = {}, theme, shopifyMode, shopifySettings, classes, actions}) {

  const {programId, action, cardNumber} = useParams();

  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [pricing, setPricing] = useState(null);
  const [selectedCardDesignIndex, setSelectedCardDesignIndex] = useState(0);
  const [defaultCardDesignId, setDefaultCardDesignId] = useState(null);
  const [cardDesign, setCardDesign] = useState(null);
  const [cardDesigns, setCardDesigns] = useState([]);
  const [section, setSection] = useState(CardCheckoutSections.ORDER);
  const [amount, setAmount] = useState(0);
  const [balance, setBalance] = useState(0);
  const [senderName, setSenderName] = useState(null);
  const [senderEmail, setSenderEmail] = useState(null);
  const [message, setMessage] = useState(null);
  const [recipientName, setRecipientName] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState(null);
  const [sendToSender, setSendToSender] = useState(false);
  const [deliveryTimeType, setDeliveryTimeType] = useState(DeliverTimes.NOW);
  const [deliveryDateTime, setDeliveryDateTime] = useState(new Date());
  const [initializingCloverPaymentConfig, setInitializingCloverPaymentConfig] = useState(true);
  const [initializingSquarePaymentConfig, setInitializingSquarePaymentConfig] = useState(true);
  const [order, setOrder] = useState(null);
  const [checkoutAction, setCheckoutAction] = useState(resolveCheckoutAction(action));
  const [giftCardNumber, setGiftCardNumber] = useState(resolveGiftCardNumber(cardNumber));
  const [giftCardDetails, setGiftCardDetails] = useState(null);
  const [idempotencyKey, setIdempotencyKey] = useState(uuid());

  const [errors, setErrors] = useState(new Errors());

  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    setLoadingConfig(true);

    let queryParams = new URLSearchParams(document.location.search.substring(1));
    let mode = queryParams.get("mode");
    if (mode === 'shopify') {
      actions.setShopifyMode(true);
    }

    const checkoutConfigJson = document.getElementById("checkout-config");
    const checkoutErrorJson = document.getElementById("checkout-error");
    if (checkoutErrorJson) {
      const checkoutError = JSON.parse(checkoutErrorJson.text);
      setErrors(new Errors().addError("config", checkoutError.errorCode));
      setLoadingConfig(false);
    }
    else if (checkoutConfigJson) {
      let checkoutConfig = JSON.parse(checkoutConfigJson.text);
      if(checkoutConfig.paymentConfig) {
        switch(checkoutConfig.paymentConfig.paymentProvider) {
          case 'CLOVER':
            checkoutConfig.cloverPaymentConfig = checkoutConfig.paymentConfig;
            break;
          case 'SQUARE':
            checkoutConfig.squarePaymentConfig = checkoutConfig.paymentConfig;
            break;
        }
      }
      setConfig(checkoutConfig);
      setCardDesigns(checkoutConfig.cardDesigns);
      setCardDesign(checkoutConfig.cardDesigns[selectedCardDesignIndex]);
      const presetBalances = checkoutConfig.programTypeConfig.presetBalances;
      if(presetBalances && presetBalances.length > 0) {
        setAmount(presetBalances[0]);
        setBalance(presetBalances[0]);
      }
      actions.updateCheckoutSettings(checkoutConfig.onlineCheckoutSettings);
      actions.resolveLocaleFromProgram(checkoutConfig.programLocale);
      setLoadingConfig(false);
    } else {
      CheckoutAPI.getCheckoutConfig(programId, locale, mode)
        .then((response) => {
          let checkoutConfig = response;
          if(checkoutConfig.paymentConfig) {
            switch(checkoutConfig.paymentConfig.paymentProvider) {
              case 'CLOVER':
                checkoutConfig.cloverPaymentConfig = checkoutConfig.paymentConfig;
                break;
              case 'SQUARE':
                checkoutConfig.squarePaymentConfig = checkoutConfig.paymentConfig;
                break;
            }
          }
          setConfig(checkoutConfig);
          setCardDesigns(checkoutConfig.cardDesigns);
          setCardDesign(checkoutConfig.cardDesigns[selectedCardDesignIndex]);
          const presetBalances = checkoutConfig.programTypeConfig.presetBalances;
          if(presetBalances && presetBalances.length > 0) {
            setAmount(presetBalances[0]);
            setBalance(presetBalances[0]);
          }
          actions.updateCheckoutSettings(checkoutConfig.onlineCheckoutSettings);
          actions.resolveLocaleFromProgram(checkoutConfig.programLocale);
          setLoadingConfig(false);
        })
        .catch((errorResponse) => {
          setErrors(new Errors().addError("config", errorResponse.errorCode));
          setLoadingConfig(false);
        });
    }

    if (window.parent) {
      window.parent.postMessage('ready', '*');
    }

    setDefaultCardDesignId(resolveCardDesignFromUrl());
  }, []);

  useEffect(() => {
    if (config === null) {
      return;
    }

    const {programTypeConfig} = config;
    const {presetBalances} = programTypeConfig;

    if (cardDesign) {
      // Set the card amount if the card design has a fixed amount
      let amountUpdate = null;
      if (cardDesign.fixedAmount && cardDesign.fixedAmount > 0) {
        amountUpdate = cardDesign.fixedAmount;
      }
      if(amountUpdate) {
        setAmount(amountUpdate);
        setBalance(amountUpdate);
      }
    }
  }, [cardDesign]);

  useEffect(() => {
    if (config === null) {
      return;
    }

    let pricingRequest = getPricingRequest();

    CheckoutAPI.getCheckoutPricing(pricingRequest)
      .then((response) => {
        setPricing(response);
        setBalance(response.balance);
        setErrors(new Errors().clear('amount'));
      })
      .catch((errorResponse) => {
        handleGetCheckoutPricingErrorResponse(errorResponse);
      });
  }, [amount, senderEmail]);

  useEffect(() => {
    if (config && config.cloverPaymentConfig && initializingCloverPaymentConfig) {
      if(config.cloverPaymentConfig.useDevPay) {
        setInitializingCloverPaymentConfig(false);
      }  else {
        loadScript(config.cloverPaymentConfig.cloverSdkUrl, (loadScriptError, script) => {
          if (loadScriptError) {
            // Checkout is closed, something is wrong;
            console.error('failed to load clover sdk');
          } else {
            setInitializingCloverPaymentConfig(false);
          }
        });
      }
    } else if(config && config.squarePaymentConfig && initializingSquarePaymentConfig) {
      loadScript(config.squarePaymentConfig.squareSdkUrl, (loadScriptError, script) => {
        if (loadScriptError) {
          // Checkout is closed, something is wrong;
          console.error('failed to load square sdk');
        } else {
          setInitializingSquarePaymentConfig(false);
        }
      });
    }
  }, [config]);

  useEffect(() => {
    if(shopifySettings) {
      if(shopifySettings.selectedAmount) {
        setAmount(shopifySettings.selectedAmount);
      }
      if(shopifySettings.selectedCardDesignId) {
        const index = cardDesigns.findIndex((cardDesign) => cardDesign.id === shopifySettings.selectedCardDesignId);
        if(index >= 0) {
          setCardDesign(cardDesigns[index]);
          setSelectedCardDesignIndex(index);
        }
      }
    }
  }, [shopifySettings]);

  useEffect(() => {
    setErrors(errors.clear('senderName'));
  }, [senderName]);

  useEffect(() => {
    setErrors(errors.clear('senderEmail'));
  }, [senderEmail]);

  useEffect(() => {
    setErrors(errors.clear('recipientName'));
  }, [recipientName]);

  useEffect(() => {
    setErrors(errors.clear('recipientEmail'));
  }, [recipientEmail]);

  useEffect(() => {
    setErrors(errors.clear('giftCardNumber'));
    setGiftCardDetails(null);
  }, [giftCardNumber]);

  useEffect(() => {
    refreshIdempotencyKey();
  }, [pricing]);

  function getPricingRequest() {
    return buildPricingRequest({
      programId: config.programId,
      amount,
      senderEmail,
      recipientEmail,
      cardDesignId: cardDesign ? cardDesign.id : null,
      locale,
      action: checkoutAction,
      accountId: giftCardDetails ? giftCardDetails.accountId : null
    });
  }

  function handleGetCheckoutPricingErrorResponse(errorResponse) {
    switch (errorResponse.errorCode) {
      case CardCheckoutErrorCodes.MAX_BALANCE_EXCEEDED :
        setErrors(new Errors().addError("amount", errorResponse.errorMessage));
        break;
      case CardCheckoutErrorCodes.MINIMUM_TOPUP_AMOUNT :
        setErrors(new Errors().addError("amount", errorResponse.errorMessage));
        break;
      default:
        setErrors(new Errors().addError("amount", "An unexpected error occurred."));
    }
  }

  function handleCheckoutActionChange(newCheckoutAction) {
    if (checkoutAction !== newCheckoutAction) {
      setCheckoutAction(newCheckoutAction);
      let url = location.pathname;
      let prefix = '';
      if(url.includes('/gift-card')) {
        prefix = '/gift-card';
      } else if(url.endsWith('/card-checkout')) {
        prefix = '/card-checkout';
      }
      switch(newCheckoutAction) {
        case CheckoutActions.NEW_CARD:
          history.push(prefix + '/' + config.programId + '/order');
          break;
        case CheckoutActions.RELOAD_CARD:
          history.push(prefix + '/' + config.programId + '/reload');
          break;
      }
    }
  }

  function resolveCheckoutAction(action) {
    switch(action) {
      case 'reload':
        return CheckoutActions.RELOAD_CARD;
      case 'order':
      default:
        return CheckoutActions.NEW_CARD;
    }
  }

  function resolveGiftCardNumber(cardNumber) {
    if(cardNumber === 'order' || cardNumber === 'reload') {
      return null;
    }
    return cardNumber;
  }

  function handleOnCheckBalanceClick() {
    if(!giftCardNumber || giftCardNumber.length === 0) {
      setErrors(new Errors().addError('giftCardNumber', 'error.gift-card-number-required'));
      return;
    }

    let sanitizedGiftCardNumber =  giftCardNumber.replaceAll('-', '').replaceAll(' ', '');

    CheckoutAPI.checkBalance({
      programId: config.programId,
      cardNumber: sanitizedGiftCardNumber
    })
      .then((response) => {
          setGiftCardDetails(response);
      })
      .catch((errorResponse) => {
         setErrors(new Errors().addError('giftCardNumber', errorResponse.errorMessage));
      });
  }

  function handleOnContinueClick() {
    if(checkoutAction === CheckoutActions.NEW_CARD) {
      validateOrder(senderName, senderEmail, amount, sendToSender, recipientName, recipientEmail, deliveryTimeType, deliveryDateTime)
        .then(() => {
          if (shopifyMode) {
            window.parent.postMessage({
              shopifyAddToCart: {
                senderName,
                senderEmail,
                amount,
                sendToSender,
                recipientName,
                recipientEmail,
                message,
                deliveryTimeType,
                deliveryDateTime: deliveryDateTime.toISOString(),
                cardDesignId: cardDesigns[selectedCardDesignIndex] ? cardDesigns[selectedCardDesignIndex].id : null
              }
            }, '*');
          } else if(config.shopifyPaymentConfig){
            const createExternalCheckoutRequest = {
              programId: config.programId,
              connectedAccountId: config.shopifyPaymentConfig.connectedAccountId,
              senderName,
              senderEmail,
              amount,
              sendToSender,
              recipientName,
              recipientEmail,
              deliverTime: deliveryTimeType,
              deliverOn: deliveryDateTime.toISOString(),
              cardDesignId: cardDesigns[selectedCardDesignIndex] ? cardDesigns[selectedCardDesignIndex].id : null,
              locale
            };

            CheckoutAPI.createExternalCheckout(createExternalCheckoutRequest)
              .then((externalCheckout) => {
                document.location = externalCheckout.checkoutUrl;
              })
              .catch((errorResponse) => {

              });
          } else {
            setSection(CardCheckoutSections.PAYMENT);
          }
        })
        .catch((validationErrors) => {
          setErrors(validationErrors);
        });
    } else if(checkoutAction === CheckoutActions.RELOAD_CARD) {
      validateReload(giftCardNumber)
        .then(() => {
          if(giftCardDetails == null) {
            let sanitizedGiftCardNumber =  giftCardNumber.replaceAll('-', '').replaceAll(' ', '');

            CheckoutAPI.checkBalance({
              programId: config.programId,
              cardNumber: sanitizedGiftCardNumber
            })
              .then((response) => {
                setGiftCardDetails(response);
                let pricingRequest = getPricingRequest();
                pricingRequest.accountId = response.accountId;
                CheckoutAPI.getCheckoutPricing(pricingRequest)
                  .then((response) => {
                    setPricing(response);
                    setBalance(response.balance);
                    setErrors(new Errors().clear('amount'));
                    setSection(CardCheckoutSections.PAYMENT);
                  })
                  .catch((errorResponse) => {
                    handleGetCheckoutPricingErrorResponse(errorResponse);
                  });
              })
              .catch((errorResponse) => {
                setErrors(new Errors().addError('giftCardNumber', errorResponse.errorMessage));
              });
          } else {
            setSection(CardCheckoutSections.PAYMENT);
          }
        })
        .catch((validationErrors) => {
          setErrors(validationErrors);
        });
    }
  }

  function handleCardDesignChange(newCardDesign, newCardDesignIndex) {
    setCardDesign(newCardDesign);
    setSelectedCardDesignIndex(newCardDesignIndex);
  }

  function handleOnEditOrder() {
    setSection(CardCheckoutSections.ORDER);
  }

  function handleOnPay(paymentToken, recaptchaToken) {
    let checkoutPurchaseRequest = {
      action: checkoutAction,
      programId: config.programId,
      amount,
      proposedAmountDue: pricing.amountDue,
      paymentToken,
      locale,
      idempotencyKey,
      recaptchaToken
    };

    if(checkoutAction === CheckoutActions.NEW_CARD) {
      checkoutPurchaseRequest = Object.assign(checkoutPurchaseRequest, {
        recipientName: sendToSender ? senderName : recipientName,
        recipientEmail: sendToSender ? senderEmail : recipientEmail,
        senderName,
        senderEmail,
        sendToSender,
        message,
        cardDesignId: cardDesign.id,
        deliverTime: deliveryTimeType,
        deliverOn: deliveryDateTime,
      });
    }
    if(checkoutAction === CheckoutActions.RELOAD_CARD) {
      checkoutPurchaseRequest = Object.assign(checkoutPurchaseRequest, {
        accountId: giftCardDetails.accountId
      });
    }

    return new Promise((resolve, reject) => {
      CheckoutAPI.submitCheckout(checkoutPurchaseRequest)
        .then((response) => {
          try {
            setOrder(response);
            setSection(CardCheckoutSections.COMPLETE);
            refreshIdempotencyKey();
            resolve();
          } catch(e) {
            reject(e);
          }
        })
        .catch((e) => {
          if (e.code >= 9000 && e.code <= 9100) {
            refreshIdempotencyKey();
          }
          reject(e);
        });
    });
  }

  function refreshIdempotencyKey() {
    setIdempotencyKey(uuid());
  }

  function handleBuyAnotherCard() {
    setSection(CardCheckoutSections.ORDER);
    setGiftCardDetails(null);
    setRecipientName(null);
    setRecipientEmail(null);
    setMessage(null);
    refreshIdempotencyKey();
  }

  function renderOrderForm() {
    let presetAmounts = checkoutSettings.amount.presetAmounts;
    if(shopifyMode && shopifySettings && shopifySettings.presetAmounts) {
      presetAmounts = shopifySettings.presetAmounts;
    } else if(config.shopifyPaymentConfig && config.shopifyPaymentConfig.denominations) {
      presetAmounts = config.shopifyPaymentConfig.denominations;
    }

    let customAmountAllowed = true;
    if (shopifyMode) {
      customAmountAllowed = false;
    }
    if (config.shopifyPaymentConfig) {
      customAmountAllowed = false;
    }

    return (
      <OrderForm
        cardDesigns={cardDesigns}
        selectedCardDesignIndex={selectedCardDesignIndex}
        balance={balance}
        amount={amount}
        currencyCode={config.programTypeConfig.currency}
        senderName={senderName}
        senderEmail={senderEmail}
        recipientName={recipientName}
        recipientEmail={recipientEmail}
        sendToSender={sendToSender}
        deliveryTimeType={deliveryTimeType}
        deliveryDateTime={deliveryDateTime}
        message={message}
        incentives={config.incentives}
        applicableIncentive={pricing ? pricing.incentive : null}
        cardDesign={cardDesign}
        errors={errors}
        checkoutSettings={checkoutSettings}
        presetAmounts={presetAmounts}
        customAmountAllowed={customAmountAllowed}
        locale={locale}
        action={checkoutAction}
        giftCardNumber={giftCardNumber}
        giftCardDetails={giftCardDetails}
        onCardDesignChange={handleCardDesignChange}
        onAmountChange={setAmount}
        onSenderNameChange={setSenderName}
        onSenderEmailChange={setSenderEmail}
        onSendToSenderChange={setSendToSender}
        onRecipientNameChange={setRecipientName}
        onRecipientEmailChange={setRecipientEmail}
        onMessageChange={setMessage}
        onDeliveryDateTimeChange={setDeliveryDateTime}
        onDeliveryTimeTypeChange={setDeliveryTimeType}
        onCheckoutActionChange={handleCheckoutActionChange}
        onContinue={handleOnContinueClick}
        onGiftCardNumberChange={setGiftCardNumber}
        onCheckBalance={handleOnCheckBalanceClick}
      />
    );
  }

  function renderPaymentForm() {
    if (shopifyMode) {
      return null;
    }

    let paymentForm = null;
    if (config.cloverPaymentConfig || config.squarePaymentConfig) {
      paymentForm = (
        <GoogleReCaptchaProvider
          reCaptchaKey={config.recaptchaKey}
        >
          <PaymentForm
            cloverPaymentConfig={config.cloverPaymentConfig}
            squarePaymentConfig={config.squarePaymentConfig}
            initializingCloverPaymentConfig={initializingCloverPaymentConfig}
            initializingSquarePaymentConfig={initializingSquarePaymentConfig}
            checkoutSettings={checkoutSettings}
            errors={errors}
            visible={section === CardCheckoutSections.PAYMENT}
            locale={locale}
            amountDue={{amount, currencyCode: config.programTypeConfig.currency}}
            amount={amount}
            balance={balance}
            currencyCode={config.programTypeConfig.currency}
            cardDesign={cardDesign}
            recipientName={recipientName}
            recipientEmail={recipientEmail}
            sendToSender={sendToSender}
            senderName={senderName}
            senderEmail={senderEmail}
            message={message}
            deliveryTimeType={deliveryTimeType}
            deliveryDateTime={deliveryDateTime}
            pricing={pricing}
            action={checkoutAction}
            giftCardDetails={giftCardDetails}
            idempotencyKey={idempotencyKey}
            onEditOrder={handleOnEditOrder}
            onPay={handleOnPay}
          />
        </GoogleReCaptchaProvider>
      );
    }
    return paymentForm;
  }

  function renderConfirmationForm() {
    return (
      <ConfirmationForm
        checkoutSettings={checkoutSettings}
        order={order}
        onBuyAnotherCard={handleBuyAnotherCard}
      />
    );
  }

  function renderTestModeBanner() {
    if (config && config.testMode) {
      return (
        <div className={classes.testModeBanner}>
          <FormattedMessage id="header.program-test-mode"/>
        </div>
      );
    }
    return null;
  }

  function renderLoadingConfig() {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress className={classes.loading}/>
      </div>
    );
  }

  function renderCheckoutError() {
    let messageId = 'error.unknown';

    switch (errors.getError('config')) {
      case CardCheckoutErrorCodes.PROGRAM_NOT_FOUND:
        messageId = 'error.program-not-found';
        break;
      case CardCheckoutErrorCodes.PROGRAM_NOT_ACTIVE:
        messageId = 'error.program-not-active';
        break;
      case CardCheckoutErrorCodes.CARD_DESIGN_NOT_FOUND:
        messageId = 'error.no-card-design';
        break;
      case CardCheckoutErrorCodes.NO_PAYMENT_GATEWAY_CONNECTED:
        messageId = 'error.no-payment-gateway';
        break;
      case CardCheckoutErrorCodes.CHECKOUT_DISABLED:
        messageId = 'error.checkout-disabled';
        break;
      case CardCheckoutErrorCodes.CHECKOUT_UNAVAILABLE:
        messageId = 'error.unknown';
        break;
      default:
        messageId = errors.getError('config');
    }

    return (
      <div className={classes.loadingContainer}>
        <SvgIcon
          className={classes.svgIcon}
          htmlColor="#303F9F"
          viewBox="0 0 64 64">
          <line fill="none" stroke="#303F9F" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10"
                strokeWidth="2" x1="11" x2="11" y1="32" y2="29"/>
          <polyline fill="none" points="  53,50 53,55 11,55 11,50 " stroke="#303F9F" strokeLinecap="round"
                    strokeLinejoin="round" strokeMiterlimit="10" strokeWidth="2"/>
          <line fill="none" stroke="#303F9F" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10"
                strokeWidth="2" x1="53" x2="53" y1="29" y2="32"/>
          <path
            d="  M12.1,9L5,20.1v1.9c0,2.1,2.7,3.7,6,3.7c2.3,0,4.2-0.8,5.2-1.9c1,1.1,3,1.9,5.3,1.9s4.2-0.8,5.3-1.9c1,1.1,3,1.9,5.3,1.9  c2.3,0,4.2-0.8,5.3-1.9c1,1.1,3,1.9,5.3,1.9s4.2-0.8,5.3-1.9c1,1.1,3,1.9,5.2,1.9c3.3,0,6-1.7,6-3.7v-1.9L51.9,9H12.1z"
            fill="none" stroke="#303F9F" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10"
            strokeWidth="2"/>
          <line fill="none" stroke="#303F9F" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10"
                strokeWidth="2" x1="8" x2="56" y1="21" y2="21"/>
          <line fill="none" stroke="#303F9F" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10"
                strokeWidth="2" x1="26.8" x2="27.7" y1="18.2" y2="11.8"/>
          <polyline fill="none" points="  47,36 42,36 42,46 47,46 " stroke="#303F9F" strokeLinecap="round"
                    strokeLinejoin="round" strokeMiterlimit="10" strokeWidth="2"/>
          <line fill="none" stroke="#303F9F" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10"
                strokeWidth="2" x1="42" x2="46" y1="41" y2="41"/>
          <path d="  M30,43c0,1.7-1.3,3.1-3,3.1s-3-1.4-3-3.1v-4.1c0-1.7,1.3-3.1,3-3.1s3,1.4,3,3.1V43z" fill="none"
                stroke="#303F9F" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeWidth="2"/>
          <path
            d="  M13.9,43.9C13.5,45.1,12.4,46,11,46c-1.7,0-3-1.4-3-3.1v-4.1c0-1.7,1.3-3.1,3-3.1c1.3,0,2.3,0.8,2.8,1.9"
            fill="none" stroke="#303F9F" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10"
            strokeWidth="2"/>
          <line fill="none" stroke="#303F9F" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10"
                strokeWidth="2" x1="16.8" x2="18.8" y1="18.2" y2="11.8"/>
          <line fill="none" stroke="#303F9F" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10"
                strokeWidth="2" x1="36.7" x2="35.8" y1="18.2" y2="11.8"/>
          <line fill="none" stroke="#303F9F" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10"
                strokeWidth="2" x1="46.7" x2="44.7" y1="18.2" y2="11.8"/>
          <path d="  M39,38.8c0-1.7-1.3-3.1-3-3.1s-3,1.4-3,3.1c0,1.2,6,2.4,6,4.1S37.7,46,36,46s-3-1.4-3-3.1" fill="none"
                stroke="#303F9F" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeWidth="2"/>
          <polyline fill="none" points="  17,36 17,46 22,46 " stroke="#303F9F" strokeLinecap="round"
                    strokeLinejoin="round" strokeMiterlimit="10" strokeWidth="2"/>
          <path d="  M50,46V35.8c0,0,6.2-1.1,6.2,5.1C56.2,47.1,50,46,50,46z" fill="none" stroke="#303F9F"
                strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeWidth="2"/>
        </SvgIcon>
        <Typography className={classes.checkoutClosedHeader} variant={'h3'}>
          <FormattedMessage id="checkout-closed"/>
        </Typography>
        <Typography className={classes.checkoutClosedMessage} variant={'body1'}>
          <FormattedMessage id={messageId}/>
        </Typography>
      </div>
    );
  }

  function renderBusinessBanner() {
    return (
      <BusinessBanner
        locations={config.locations}
        checkoutSettings={checkoutSettings}/>
    );
  }

  function renderActiveSection() {
    return (
      <>
        {section === CardCheckoutSections.ORDER && renderOrderForm()}
        {renderPaymentForm()}
        {section === CardCheckoutSections.COMPLETE && renderConfirmationForm()}
      </>
    );
  }

  const showLoading = loadingConfig;
  const showCheckoutError = errors.hasError('config') || errors.hasError('platformError');
  const checkoutConfigLoaded = !loadingConfig && config !== null;
  const showActiveSection = !showCheckoutError && checkoutConfigLoaded;
  const showCardHeader = !showCheckoutError && checkoutConfigLoaded;

  let bodySection = null;
  if (checkoutSettings) {
    bodySection = checkoutSettings.body;
  }

  return (

    <MuiThemeProvider theme={theme}>
      <IntlProvider locale={locale} messages={messages[locale]}>
        <StyleableSection className={classes.body} section={bodySection}>
          {renderTestModeBanner()}
          {showLoading && renderLoadingConfig()}
          {showCheckoutError && renderCheckoutError()}
          {showCardHeader && renderBusinessBanner()}
          {showActiveSection && renderActiveSection()}
        </StyleableSection>
      </IntlProvider>
    </MuiThemeProvider>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Checkout));
